import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Users, Plus, Pencil, Trash2, Phone, UserCheck, UserX, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function WorkersPage() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    password: ""
  });

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(`${API}/workers`);
      setWorkers(response.data);
    } catch (error) {
      toast.error("Erro ao carregar trabalhadores");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedWorker) {
        // Update
        await axios.put(`${API}/workers/${selectedWorker.id}`, {
          name: formData.name,
          phone: formData.phone.replace(/\s/g, "")
        });
        toast.success("Trabalhador atualizado com sucesso!");
      } else {
        // Create
        await axios.post(`${API}/workers`, {
          name: formData.name,
          phone: formData.phone.replace(/\s/g, ""),
          password: formData.password,
          role: "worker"
        });
        toast.success("Trabalhador criado com sucesso!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar trabalhador");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/workers/${selectedWorker.id}`);
      toast.success("Trabalhador removido com sucesso!");
      setDeleteDialogOpen(false);
      setSelectedWorker(null);
      fetchWorkers();
    } catch (error) {
      toast.error("Erro ao remover trabalhador");
    }
  };

  const openEditDialog = (worker) => {
    setSelectedWorker(worker);
    setFormData({
      name: worker.name,
      phone: worker.phone || "",
      password: ""
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (worker) => {
    setSelectedWorker(worker);
    setDeleteDialogOpen(true);
  };

  const openPasswordDialog = (worker) => {
    setSelectedWorker(worker);
    setNewPassword("");
    setPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("A senha deve ter pelo menos 4 caracteres");
      return;
    }
    try {
      await axios.put(`${API}/workers/${selectedWorker.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success("Senha alterada com sucesso!");
      setPasswordDialogOpen(false);
      setSelectedWorker(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    }
  };

  const resetForm = () => {
    setSelectedWorker(null);
    setFormData({ name: "", phone: "", password: "" });
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
  };

  const toggleWorkerStatus = async (worker) => {
    try {
      await axios.put(`${API}/workers/${worker.id}`, {
        is_active: !worker.is_active
      });
      toast.success(`Trabalhador ${worker.is_active ? "desativado" : "ativado"}!`);
      fetchWorkers();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="workers-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-manrope">Trabalhadores</h1>
            <p className="text-gray-500 mt-1">{workers.length} de 20 trabalhadores cadastrados</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="btn-primary text-white font-semibold"
                disabled={workers.length >= 20}
                data-testid="add-worker-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-gray-800">
                  {selectedWorker ? "Editar Trabalhador" : "Novo Trabalhador"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Nome</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nome completo"
                    required
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="worker-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Número de Celular</Label>
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center px-3 bg-gray-100 border border-gray-200 rounded-md text-gray-600 font-medium text-sm">
                      +41
                    </div>
                    <Input
                      type="tel"
                      value={formatPhone(formData.phone)}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })}
                      placeholder="782 529 025"
                      required
                      maxLength={11}
                      className="bg-gray-50 border-gray-200 text-gray-800"
                      data-testid="worker-phone-input"
                    />
                  </div>
                </div>
                {!selectedWorker && (
                  <div className="space-y-2">
                    <Label className="text-gray-700">Senha</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="bg-gray-50 border-gray-200 text-gray-800"
                      data-testid="worker-password-input"
                    />
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50">
                      Cancelar
                    </Button>
                  </DialogClose>
                  <Button type="submit" className="flex-1 btn-primary text-white font-semibold" data-testid="save-worker-btn">
                    {selectedWorker ? "Salvar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Workers Grid */}
        {workers.length === 0 ? (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardContent className="py-16">
              <div className="empty-state">
                <Users className="empty-state-icon" />
                <p className="empty-state-title">Nenhum trabalhador cadastrado</p>
                <p className="empty-state-description">
                  Clique em "Adicionar" para cadastrar o primeiro trabalhador
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workers.map((worker) => (
              <Card key={worker.id} className="bg-white border-gray-100 shadow-sm card-hover" data-testid={`worker-card-${worker.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#2ECC40] to-[#F5A623] flex items-center justify-center text-white font-bold text-lg">
                        {worker.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{worker.name}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Phone size={12} />
                          +41 {formatPhone(worker.phone)}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      worker.is_active !== false ? "badge-active" : "badge-inactive"
                    }`}>
                      {worker.is_active !== false ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      onClick={() => openEditDialog(worker)}
                      data-testid={`edit-worker-${worker.id}`}
                    >
                      <Pencil size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      onClick={() => openPasswordDialog(worker)}
                      data-testid={`reset-password-${worker.id}`}
                    >
                      <KeyRound size={14} className="mr-1" />
                      Senha
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      onClick={() => toggleWorkerStatus(worker)}
                      data-testid={`toggle-worker-${worker.id}`}
                    >
                      {worker.is_active !== false ? (
                        <UserX size={14} />
                      ) : (
                        <UserCheck size={14} />
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => openDeleteDialog(worker)}
                      data-testid={`delete-worker-${worker.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white border-gray-200">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-gray-800">Remover trabalhador?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-500">
                Esta ação não pode ser desfeita. Isso removerá permanentemente {selectedWorker?.name} do sistema.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-gray-200 text-gray-600 hover:bg-gray-50">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-delete-worker"
              >
                Remover
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Password Reset Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-800">
                Alterar Senha - {selectedWorker?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Nova Senha</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite a nova senha"
                  className="bg-gray-50 border-gray-200 text-gray-800"
                  data-testid="new-password-input"
                />
                <p className="text-xs text-gray-500">Mínimo 4 caracteres</p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50"
                  onClick={() => setPasswordDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  className="flex-1 btn-primary text-white font-semibold"
                  onClick={handleResetPassword}
                  data-testid="confirm-reset-password"
                >
                  <KeyRound size={16} className="mr-2" />
                  Alterar Senha
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
